import { FieldValidator } from 'components/Utils/Validation.types';
import type { Dispatch, SetStateAction } from 'react';

type SetErrors = Dispatch<SetStateAction<Record<string, string | boolean>>>;

const validate = (validators: FieldValidator<unknown>[], setErrors: SetErrors): boolean => {
  const errors: Record<string, string | boolean> = {};
  let isValid = true;

  validators.forEach((v) => {
    const fieldError = 'getErrorMessage' in v ? v.getErrorMessage(v.fieldValue) : !v.isValid(v.fieldValue);

    errors[v.fieldName] = fieldError;
    isValid = isValid && !fieldError;
  });

  setErrors((prev) => ({ ...prev, ...errors }));

  return isValid;
};

const isNotNullOrUndefined = (item: unknown) => item != null;
const isNonEmptyArray = (items?: []) => Array.isArray(items) && items.length > 0;

const isString = (val?: string): val is string => typeof val === 'string';
const isStringWithMinLength = (val: string, minLength = 2): val is string => isString(val) && val.length >= minLength;
const isNumeric = (val: unknown): val is Number => val != null && typeof val === 'number' && !isNaN(val);

type DateArgs = {
  day?: number;
  month?: number;
  year?: number;
};

const getDate = ({ day, month, year }: DateArgs) => {
  if (typeof day !== 'number') return undefined;
  if (typeof month !== 'number') return undefined;
  if (typeof year !== 'number') return undefined;

  return new Date(`${year}-${month}-${day}`);
};

const isDateValid = (args: DateArgs) => getDate(args)?.valueOf() >= 0;

const isDateValidAndPast = (args: DateArgs) => {
  const dateMillis = getDate(args)?.valueOf();
  return dateMillis >= 0 && dateMillis < new Date().valueOf();
};

const isDateValidAndFuture = (args: DateArgs) => {
  const dateMillis = getDate(args)?.valueOf();
  return dateMillis > new Date().valueOf();
};

const isDateValidAnd18Yo = (args: DateArgs) => {
  const dateMillis = getDate(args)?.valueOf();

  if (!isNumeric(dateMillis)) return false;

  const today = new Date().getFullYear();
  const limit = new Date(dateMillis).setFullYear(today - 18);

  return dateMillis <= limit;
};

// In the format of +614xxxxxxxx, 614xxxxxxxx or 04xxxxxxxx
const isAustralianMobileNumber = (mobileNumber: string) => {
  const re = /^(?:0|\+?61)[ -]?4[ -]?\d{2}[ -]?\d{2}[ -]?\d[ -]?\d{3}$/;
  return mobileNumber && re.test(String(mobileNumber));
};

const isAustralianPhoneNumber = (phoneNumber: string) => {
  const re = /^\(?((0|\+61|61)([24378]))?\)?([ -])?\d{2}([ -])?\d{2}([ -])?\d([ -])?\d{3}$/i;
  return phoneNumber && re.test(String(phoneNumber));
};

const isAustralianPhoneNumberOrNull = (phoneNumber) => phoneNumber == null || isAustralianPhoneNumber(phoneNumber);

const isValidEmail = (email?: string) => {
  const re =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

  return email == null || re.test(String(email));
};

const isValidWebAddress = (webAddress?: string) => {
  const re =
    /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?\w+=\w+(&\w+=\w+)*)?$/i;

  return webAddress == null || re.test(String(webAddress));
};

const isAddressValid = (address) => {
  if (!address) {
    return false;
  } else {
    if (address.customType) {
      const a = address.value.details;
      return (
        a &&
        a.addressLine1 &&
        a.addressLine1.length > 2 &&
        a.suburb &&
        a.suburb.length > 2 &&
        a.state &&
        a.state.value !== null &&
        a.postcode &&
        a.postcode.length > 2 &&
        (!address.international || (address.international && a.country && a.country.length > 2))
      );
    } else {
      return true;
    }
  }
};

const scrollToError = () => {
  window.setTimeout(() => {
    const errorElements = document.querySelectorAll('[class*=ErrorMsg_error],[class^=FormError]');
    if (errorElements && errorElements.length > 0) {
      const yOffset = -150;
      const y = errorElements[0].getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, 100);
};

// ----- FREE TEXT VALIDATION -----------------------------------------------------------------------------------------
type TextValidationFn = (text: string, errorMessage?: string) => string | undefined;

type BuildFreeTextValidator = Omit<
  typeof FreeTextValidatorBuilder.prototype,
  '_validators' | 'validators' | 'createRestrictedCharSetValidator'
>;

class FreeTextValidatorBuilder {
  private readonly _validators = new Map<string, TextValidationFn>();

  public get validators() {
    return Array.from(this._validators.values());
  }

  /**
   * Ensures there are no unicode characters in the text.
   *
   * @param errorMessage Custom error message to use. Defaults to, 'You may not use unicode characters'.
   */
  public hasNoUnicode(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set('hasNoUnicode', (text: string) => {
      return /[^\u0000-\u00ff]/.test(text) ? errorMessage ?? 'You may not use unicode characters' : undefined;
    });

    return this;
  }

  /**
   * Ensures the text contains no "uncommon" characters. This is the loosest validator of all the in-built
   * free text validators.
   *
   * @param errorMessage Custom error message to use. Defaults to, 'The following character is not allowed in
   * this field: [first invalid character]'.
   */
  public hasCommonCharactersOnly(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set(
      'hasCommonCharactersOnly',
      this.createRestrictedCharSetValidator(/[^\w\s!@#$%&*()\-+=/'",.:;?[\]{}]+/gi, errorMessage)
    );

    return this;
  }

  /**
   * Ensures the text only contains characters allow by names registered by an Australian government authority
   * (letters, spaces, hyphens, apostrophes).
   *
   * @param errorMessage Custom error message to use. Defaults to, 'The following character is not allowed in
   * this field: [first invalid character]'.
   *
   * @see https://www.publications.qld.gov.au/dataset/rbdm-policies/resource/6f80a6e2-a991-4015-acb1-e7599f072bfb
   */
  public hasNameCharactersOnly(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set(
      'hasNameCharactersOnly',
      this.createRestrictedCharSetValidator(/[^a-z\s\-']+/gi, errorMessage)
    );
    return this;
  }

  /**
   * Ensures the text does not contain a sequence of characters that looks like a script tag.
   *
   * @param errorMessage Custom error message to use. Defaults to, 'The text you have entered is not allowed.'.
   */
  public hasNoScriptTags(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set('hasNoScriptTags', (text: string) => {
      const isInsecure = /script\s*>/i.test(text);
      return isInsecure ? errorMessage ?? 'The text you have entered is not allowed.' : undefined;
    });

    return this;
  }

  /**
   * Ensures the text does not contain a URL scheme separator (`://`).
   *
   * @param errorMessage Custom error message to use. Defaults to, 'Web links are not allowed.'.
   */
  public hasNoUrlIndicators(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set('hasNoUrlIndicators', (text: string) => {
      const hasUrlIndicator = text.indexOf('://') >= 0;
      return hasUrlIndicator ? errorMessage ?? 'Web links are not allowed.' : undefined;
    });

    return this;
  }

  /**
   * Ensures the text does not contain encoded characters (`%[a-z0-9]+`).
   *
   * @param errorMessage Custom error message to use. Defaults to, 'Text encoded values are not allowed.'.
   */
  public hasNoEncodedValues(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set('hasNoEncodedValues', (text: string) => {
      const hasEncodedValues = /%[a-z0-9]+/i.test(text);
      return hasEncodedValues ? errorMessage ?? 'Text encoded values are not allowed.' : undefined;
    });

    return this;
  }

  /**
   * Ensures the text does not contain a sequence of characters that look like they are traversing a path (for
   * example `./` or `.\`).
   *
   * @param errorMessage Custom error message to use. Defaults to, 'Paths are not allowed.'.
   */
  public hasNoPathTraversal(errorMessage?: string): BuildFreeTextValidator {
    this._validators.set('hasNoPathTraversal', (text: string) => {
      const hasPathTraversal = /\.[\/\\]/i.test(text);
      return hasPathTraversal ? errorMessage ?? 'Paths are not allowed.' : undefined;
    });

    return this;
  }

  private createRestrictedCharSetValidator =
    (regex: RegExp, errorMessage?: string, normalize = false) =>
    (text: string) => {
      const valueToCheck = text;

      // See: https://stackoverflow.com/a/37511463/127497
      if (normalize) {
        valueToCheck.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
      }

      const match = valueToCheck.match(regex);
      if (!match) return undefined;
      if (errorMessage) return errorMessage;

      if (match.length === 1) {
        const [invalidChar] = match;
        return `The following character is not allowed in this field: ${invalidChar}`;
      } else {
        const invalidChars = match.reduce((memo, match) => `${memo}, ${match}`);
        return `The following characters are not allowed in this field: ${invalidChars}`;
      }
    };
}

const validateFreeText = (build: (builder: BuildFreeTextValidator) => void): TextValidationFn => {
  const builder = new FreeTextValidatorBuilder();
  build(builder);

  const { validators } = builder;

  return (text: string, errorMessage?: string) => {
    if (text == null) return undefined;

    const validationError = validators.map((fn) => fn(text)).find((validatorResult) => !!validatorResult);

    return Boolean(validationError) ? errorMessage ?? validationError : undefined;
  };
};

export const Validation = {
  validate,
  isNotNullOrUndefined,
  isStringWithMinLength,
  isNonEmptyArray,
  isDateValid,
  isDateValidAndPast,
  isDateValidAndFuture,
  isDateValidAnd18Yo,
  isNumeric,
  isValidEmail,
  isValidWebAddress,
  isAddressValid,
  isAustralianMobileNumber,
  isAustralianPhoneNumber,
  isAustralianPhoneNumberOrNull,
  scrollToError,
  validateFreeText,

  /** @obsolete Please use `isNotNullOrUndefined` */
  isSelected: isNotNullOrUndefined,

  /** @obsolete Please use `isStringWithMinLength` */
  isCompleted: isStringWithMinLength,

  /** @obsolete Please use `isNonEmptyArray` */
  isSelectionComplete: isNonEmptyArray,

  /** @obsolete Please use `isNonEmptyArray` */
  hasAnyValue: isNonEmptyArray,
};
